'use client';

import { useRef, type ComponentProps, type ReactNode } from 'react';
import { EmbeddedComponentRegion } from '@/components/region/embedded-component-region';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';
import { FOOTER_PAYMENT_METHOD_REGION_ID } from '@/features/global-footer/constants';
import { PAYMENT_METHOD_IDS } from '@/extensions/snk/lib/payment-method-platforms';
import { cn } from '@/lib/utils';
import { LogoStack } from '@/shared-ui';
import { PaymentMethodIcon } from '../payment-method-icon';

export interface PaymentMethodIconListProps extends Omit<ComponentProps<'div'>, 'children'> {
    heading: string;
    icons?: ReactNode;
}

export const PaymentMethodIconList = ({ heading, icons, className, ...props }: PaymentMethodIconListProps) => {
    const trimmedHeading = heading.trim();
    if (!trimmedHeading) {
        return null;
    }

    return (
        <LogoStack.Root className={cn(className)} data-slot="payment-method-icon-list" {...props}>
            <LogoStack.Title className="text-primary-foreground">{trimmedHeading}</LogoStack.Title>
            <LogoStack.Wrapper>
                <div data-slot="payment-method-icon-row" className="flex flex-row flex-wrap gap-2">
                    {icons}
                </div>
            </LogoStack.Wrapper>
        </LogoStack.Root>
    );
};

function PaymentMethodsStatic({ className }: { className?: string }) {
    return (
        <PaymentMethodIconList
            className={className}
            heading="We accept"
            icons={PAYMENT_METHOD_IDS.map((method) => <PaymentMethodIcon key={method} method={method} />)}
        />
    );
}

type PaymentMethodsProps = {
    globalFooter?: Promise<ComponentWithComponentData | null>;
    className?: string;
};

export const PaymentMethods = ({ globalFooter, className }: PaymentMethodsProps) => {
    const componentRef = useRef<Promise<ComponentWithComponentData | null> | undefined>(undefined);

    if (!globalFooter) {
        return <PaymentMethodsStatic className={className} />;
    }

    if (!componentRef.current) {
        componentRef.current = globalFooter;
    }

    const component = componentRef.current;

    return (
        <div className={className}>
            <EmbeddedComponentRegion
                component={component}
                regionId={FOOTER_PAYMENT_METHOD_REGION_ID}
                fallbackElement={null}
                errorElement={null}
            />
        </div>
    );
};
